import React, { useState } from "react";
import { Box, Typography, Link, Alert, CircularProgress } from "@mui/material";
import { ArrowBack, Email } from "@mui/icons-material";

import logo from "../../assets/nwt.png";
import { useForgotPasswordMutation } from "../../services";
import { notify } from "../../components/Notification";
import {
  BrandIcon,
  BrandSection,
  ContentWrapper,
  Card,
  Container,
  CardSection,
  StyledTextField,
} from "./styles";
import Button, { ButtonTypes } from "../../components/Button";

export const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [forgotPassword] = useForgotPasswordMutation();

  const validateEmail = (email: string): boolean => {
    return /\S+@\S+\.\S+/.test(email);
  };

  const handleSubmit = async (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    if (!email.trim()) {
      setError("Please enter your email address");
      setLoading(false);
      return;
    }

    if (!validateEmail(email)) {
      setError("Please enter a valid email address");
      setLoading(false);
      return;
    }

    try {
      await forgotPassword(email).unwrap();
      setIsSubmitted(true);
      notify.success(
        "Password reset instructions have been sent to your email",
      );
    } catch {
      setError(
        "Unable to send reset email. Please try again or contact support.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResendEmail = async () => {
    setLoading(true);
    try {
      await forgotPassword(email).unwrap();
      notify.success("Password reset email has been resent");
    } catch {
      // errorToastMiddleware raises the toast.
    } finally {
      setLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <Container>
        <ContentWrapper>
          <CardSection>
            <Card elevation={0}>
              <BrandSection>
                <BrandIcon>
                  <img src={logo} alt="NWT" />
                </BrandIcon>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: "bold", color: "var(--color-primary)" }}
                  gutterBottom
                >
                  Check Your Email
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  We've sent password reset instructions to
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ fontWeight: "medium", color: "var(--color-primary)" }}
                  gutterBottom
                >
                  {email}
                </Typography>
              </BrandSection>

              <Box sx={{ textAlign: "center", mb: 4 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  If you don't see the email in your inbox, check your spam
                  folder or click below to resend.
                </Typography>

                <Button
                  type={ButtonTypes.primary}
                  isFullWidth={true}
                  size="large"
                  isLoading={loading}
                  onClick={handleResendEmail}
                >
                  {loading ? "Resending..." : "Resend Email"}
                </Button>
              </Box>

              <Box sx={{ textAlign: "center" }}>
                <Link
                  href="/"
                  underline="hover"
                  sx={{
                    fontWeight: "medium",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                    color: "var(--color-primary)",
                  }}
                >
                  <ArrowBack fontSize="small" />
                  Back to Sign In
                </Link>
              </Box>
            </Card>
          </CardSection>
        </ContentWrapper>
      </Container>
    );
  }

  return (
    <Container>
      <ContentWrapper>
        <CardSection>
          <Card elevation={0}>
            <BrandSection>
              <BrandIcon>
                <img src={logo} alt="NWT" />
              </BrandIcon>
              <Typography
                variant="h4"
                sx={{ fontWeight: "bold", color: "var(--color-primary)" }}
                gutterBottom
              >
                Reset Password
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Enter your email address and we'll send you instructions to
                reset your password
              </Typography>
            </BrandSection>

            {error && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <StyledTextField
                fullWidth
                label="Email Address"
                type="email"
                variant="outlined"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                autoComplete="email"
                autoFocus
                placeholder="Enter your email address"
                slotProps={{
                  input: {
                    startAdornment: (
                      <Box
                        sx={{ mr: 1, display: "flex", alignItems: "center" }}
                      >
                        <Email color="action" fontSize="small" />
                      </Box>
                    ),
                  },
                }}
              />
              <div style={{ marginBottom: "2rem" }}>
                <Button
                  buttonType="submit"
                  type={ButtonTypes.primary}
                  size="large"
                  isLoading={loading}
                  isFullWidth={true}
                >
                  {loading ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CircularProgress size={16} color="inherit" />
                      Sending Instructions...
                    </Box>
                  ) : (
                    "Send Reset Instructions"
                  )}
                </Button>
              </div>
            </form>

            <Box sx={{ textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Remember your password?{" "}
                <Link
                  href="/"
                  sx={{ color: "var(--color-primary)", fontWeight: "medium" }}
                  underline="hover"
                >
                  Sign In
                </Link>
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Don't have an account?{" "}
                <Link
                  href="/register"
                  sx={{ color: "var(--color-primary)", fontWeight: "medium" }}
                  underline="hover"
                >
                  Create one now
                </Link>
              </Typography>
            </Box>
          </Card>
        </CardSection>
      </ContentWrapper>
    </Container>
  );
};
